"use client";

import { localPointFromSvg } from "./scatter-svg";
import { ParentSize } from "@visx/responsive";
import { scaleBand, scaleLinear } from "@visx/scale";
import type { Transition } from "motion/react";
import { animate as animateValue, useMotionValue } from "motion/react";
import {
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useModuleRevealGate } from "@/components/motion/module-reveal-gate";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { DEFAULT_ANIMATION_EASING } from "./animation";
import type { BarProps } from "./bar";
import { topSquareCenterY } from "./bar-squares-layout";
import {
  forEachChartChild,
  isChartClipPassthrough,
  isClipExcludedComponent,
  isPostOverlayComponent,
  isUnderlayComponent,
  renderKeyedChartLayers,
  resolveChartChildElement,
} from "./chart-child-passthrough";
import {
  ChartProvider,
  type LineConfig,
  type Margin,
  type TooltipData,
} from "./chart-context";
import { isGradientDefComponent, isPatternDefComponent } from "./chart-defs";
import { shortDateFmt } from "./chart-formatters";
import {
  type ChartPhase,
  type ChartStatus,
  DEFAULT_CHART_LIFECYCLE,
  resolveRestingChartPhase,
} from "./chart-phase";
import { BarLoadingSkeleton } from "./loading-sweep";
import {
  BarProjectionProvider,
  type BarProjectionValue,
  barSlotKey,
  ProjectionFadeOut,
} from "./bar-projection-context";
import {
  ChartProjectionProvider,
  painterKey,
  painterOrder,
  type ProjectionMode,
} from "./projection";
import { extractReferenceAreaConfigs } from "./reference-area-config";
import { useScheduledTooltip } from "./use-scheduled-tooltip";
import {
  buildYScalesForLines,
  getPrimaryYScale,
  normalizeYAxisId,
  wrapSingleYScale,
} from "./y-axis-scales";

/** Skeleton bars to show when `status="loading"` and `data` is empty. */
const FALLBACK_LOADING_BARS = 12;

export type BarOrientation = "vertical" | "horizontal";

export interface BarChartProps {
  /** Data array - each item should have an x-axis key and numeric values */
  data: Record<string, unknown>[];
  /** Key in data for the categorical axis. Default: "name" */
  xDataKey?: string;
  /** Chart margins */
  margin?: Partial<Margin>;
  /** Animation duration in milliseconds. Default: 1100 */
  animationDuration?: number;
  /** CSS easing for bar grow transitions. */
  animationEasing?: string;
  /** Motion enter transition (spring or cubic-bezier tween). */
  enterTransition?: Transition;
  /** Signature of motion URL state — triggers enter replay when it changes. */
  revealSignature?: string;
  /** Aspect ratio as "width / height". Default: "2 / 1" */
  aspectRatio?: string;
  /** Additional class name for the container */
  className?: string;
  /** Gap between bar groups as a fraction of band width (0-1). Default: 0.2 */
  barGap?: number;
  /** Fixed bar width in pixels. If not set, bars auto-size to fill the band. */
  barWidth?: number;
  /** Bar chart orientation. Default: "vertical" */
  orientation?: BarOrientation;
  /** Whether to stack bars instead of grouping them. Default: false */
  stacked?: boolean;
  /** Gap between stacked bar segments in pixels. Default: 0 */
  stackGap?: number;
  /** When set, tooltip Y positions snap to the top square center (shape variant). */
  squareSnap?: { squareGap: number; groupGap?: number; fit?: boolean };
  /** Child components (Bar, Grid, ChartTooltip, etc.). Optional — omit for a
   * pure `status="loading"` skeleton. */
  children?: ReactNode;
  /** Reports reveal lifecycle for OG screenshots and loading orchestration. */
  onPhaseChange?: (phase: ChartPhase) => void;
  /** Fetch / display status. When `"loading"`, a shimmer skeleton replaces the
   * bars (no chart data required). Default: `"ready"`. */
  status?: ChartStatus;
  projection?: ProjectionMode;
  projectionDepth?: number;
}

const DEFAULT_MARGIN: Margin = { top: 40, right: 40, bottom: 40, left: 40 };

// Extract bar configs from children synchronously
function extractBarConfigs(children: ReactNode): LineConfig[] {
  const configs: LineConfig[] = [];

  forEachChartChild(children, (child) => {
    const childType = child.type as {
      displayName?: string;
      name?: string;
    };
    const componentName =
      typeof child.type === "function"
        ? childType.displayName || childType.name || ""
        : "";

    const props = child.props as BarProps | undefined;
    const isBarComponent =
      componentName === "Bar" ||
      componentName === "BarSquares" ||
      (props && typeof props.dataKey === "string" && props.dataKey.length > 0);

    if (isBarComponent && props?.dataKey) {
      // Use stroke for tooltip dot color if provided, otherwise fall back to fill
      // This allows gradient/pattern fills to have a solid dot color
      const dotColor =
        props.stroke || props.fill || "var(--chart-line-primary)";
      configs.push({
        dataKey: props.dataKey,
        stroke: dotColor,
        strokeWidth: 0,
        yAxisId: props.yAxisId,
      });
    }
  });

  return configs;
}

interface ChartInnerProps {
  width: number;
  height: number;
  data: Record<string, unknown>[];
  xDataKey: string;
  margin: Margin;
  animationDuration: number;
  animationEasing: string;
  enterTransition?: Transition;
  revealSignature?: string;
  barGap: number;
  barWidthProp?: number;
  orientation: BarOrientation;
  stacked: boolean;
  stackGap: number;
  squareSnap?: { squareGap: number; groupGap?: number; fit?: boolean };
  children: ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onPhaseChange?: (phase: ChartPhase) => void;
  status: ChartStatus;
  projection?: ProjectionMode;
  projectionDepth?: number;
}

const PROJECTION_PAD = 32;
const MAX_DEPTH_SHARE = 0.5;

function ChartInner(props: ChartInnerProps) {
  const { width, height } = props;
  if (width < 10 || height < 10) {
    return null;
  }
  return <ChartCore {...props} />;
}

const ChartCore = memo(function ChartCore({
  width,
  height,
  data,
  xDataKey,
  margin,
  animationDuration,
  animationEasing,
  enterTransition,
  revealSignature = "",
  barGap,
  barWidthProp,
  orientation,
  stacked,
  stackGap,
  squareSnap,
  children,
  containerRef,
  onPhaseChange,
  status,
  projection,
  projectionDepth,
}: ChartInnerProps) {
  const { tooltipData, setTooltipData, scheduleTooltip, clearTooltip } =
    useScheduledTooltip<TooltipData>();
  const revealGate = useModuleRevealGate();
  const holdReveal = revealGate === "held";
  const skipReveal = revealGate === "done";
  /**
   * Read through a ref, never as an effect dependency: the page marks itself
   * revealed while this grow reveal is still running, and a dependency would
   * drop the settle timer mid-flight — the bars would never reach `isLoaded`.
   */
  const skipRevealRef = useRef(skipReveal);
  skipRevealRef.current = skipReveal;
  const [isLoaded, setIsLoaded] = useState(skipReveal);
  const [revealEpoch, setRevealEpoch] = useState(0);
  const hoveredBarIndex = tooltipData?.index ?? null;
  const hoverCapable = useHoverCapable();
  const interactionElRef = useRef<SVGGElement | null>(null);
  const touchTooltipVisibleRef = useRef(false);
  const lastTappedIndexRef = useRef<number | null>(null);

  const isHorizontal = orientation === "horizontal";
  const projectionMode = isHorizontal ? undefined : projection;
  const isIsometric = projectionMode === "isometric";
  const [projectionSettled, setProjectionSettled] = useState(true);
  const reveal = useMotionValue(skipReveal ? 1 : 0);
  const slotRootRef = useRef<SVGGElement | null>(null);
  const [slots, setSlots] = useState<ReadonlyMap<string, SVGGElement>>(
    () => new Map()
  );
  const pendingLeaveRef = useRef<number | null>(null);
  const lastTappedElementRef = useRef<string | null>(null);
  const { intlLocale } = useViewerLanguage();

  // Extract bar configs synchronously from children
  const lines = useMemo(() => extractBarConfigs(children), [children]);

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Category accessor function - returns string for categorical scale
  const categoryAccessor = useCallback(
    (d: Record<string, unknown>): string => {
      const value = d[xDataKey];
      if (value instanceof Date) {
        return shortDateFmt(intlLocale).format(value);
      }
      return String(value ?? "");
    },
    [xDataKey, intlLocale]
  );

  // For compatibility with ChartContext, provide a Date-based xAccessor
  const xAccessorDate = useCallback(
    (d: Record<string, unknown>): Date => {
      const value = d[xDataKey];
      if (value instanceof Date) {
        return value;
      }
      return new Date();
    },
    [xDataKey]
  );

  // Category scale (band) - for the categorical axis
  const categoryScale = useMemo(() => {
    const domain = data.map((d) => categoryAccessor(d));
    const range: [number, number] = isHorizontal
      ? [0, innerHeight]
      : [0, innerWidth];
    return scaleBand<string>({
      range,
      domain,
      padding: barGap,
    });
  }, [innerWidth, innerHeight, data, categoryAccessor, barGap, isHorizontal]);

  // Band width for bars - use prop if provided, otherwise use scale's bandwidth
  const bandWidth = barWidthProp ?? categoryScale.bandwidth();

  // Compute max value considering stacking
  const maxValue = useMemo(() => {
    if (stacked) {
      // For stacked bars, sum all values at each data point
      let max = 0;
      for (const d of data) {
        let sum = 0;
        for (const line of lines) {
          const value = d[line.dataKey];
          if (typeof value === "number") {
            sum += value;
          }
        }
        if (sum > max) {
          max = sum;
        }
      }
      return max || 100;
    }
    // For grouped bars, find max single value
    let max = 0;
    for (const line of lines) {
      for (const d of data) {
        const value = d[line.dataKey];
        if (typeof value === "number" && value > max) {
          max = value;
        }
      }
    }
    return max || 100;
  }, [data, lines, stacked]);

  // Value scale (linear) - for the value axis
  const valueScale = useMemo(() => {
    const range = isHorizontal ? [0, innerWidth] : [innerHeight, 0];
    return scaleLinear({
      range,
      domain: [0, maxValue * 1.1],
      nice: true,
    });
  }, [innerWidth, innerHeight, maxValue, isHorizontal]);

  const yScales = useMemo(() => {
    if (isHorizontal) {
      return wrapSingleYScale(valueScale);
    }
    return buildYScalesForLines({
      lines,
      data,
      innerHeight,
      resolveDomain: (dataKeys) => {
        let max = 0;
        for (const d of data) {
          for (const key of dataKeys) {
            const value = d[key];
            if (typeof value === "number" && value > max) {
              max = value;
            }
          }
        }
        return [0, (max || 100) * 1.1];
      },
    });
  }, [data, innerHeight, isHorizontal, lines, valueScale]);

  const primaryYScale = getPrimaryYScale(yScales, valueScale);

  // Compute stack offsets for stacked bars
  const stackOffsets = useMemo(() => {
    if (!stacked) {
      return undefined;
    }
    const offsets = new Map<number, Map<string, number>>();
    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      if (!d) {
        continue;
      }
      const pointOffsets = new Map<string, number>();
      let cumulative = 0;
      for (const line of lines) {
        pointOffsets.set(line.dataKey, cumulative);
        const value = d[line.dataKey];
        if (typeof value === "number") {
          cumulative += value;
        }
      }
      offsets.set(i, pointOffsets);
    }
    return offsets;
  }, [data, lines, stacked]);

  // Column width for tooltip indicator
  const columnWidth = useMemo(() => {
    if (data.length < 1) {
      return 0;
    }
    return isHorizontal ? innerHeight / data.length : innerWidth / data.length;
  }, [innerWidth, innerHeight, data.length, isHorizontal]);

  // Pre-compute labels for ticker animation
  const dateLabels = useMemo(
    () => data.map((d) => categoryAccessor(d)),
    [data, categoryAccessor]
  );

  // Create a fake time scale for compatibility with ChartContext
  const fakeTimeScale = useMemo(() => {
    const now = Date.now();
    const start = now - data.length * 24 * 60 * 60 * 1000;
    const scale = {
      ...categoryScale,
      domain: () => [new Date(start), new Date(now)],
      range: () => [0, innerWidth] as [number, number],
      invert: (x: number) => new Date(start + (x / innerWidth) * (now - start)),
      copy: () => scale,
    };
    return scale;
  }, [categoryScale, innerWidth, data.length]);

  // Animation timing — replay when motion settings change
  // biome-ignore lint/correctness/useExhaustiveDependencies: revealSignature
  useEffect(() => {
    // The page this chart sits on is not in view yet — the grow reveal waits
    // instead of playing to an audience that cannot see it.
    if (holdReveal) {
      return;
    }
    if (skipRevealRef.current) {
      setIsLoaded(true);
      return;
    }
    setRevealEpoch((n) => n + 1);
    setIsLoaded(false);
    // While loading, hold the skeleton (no reveal, no interaction). When
    // status flips to "ready" this effect re-runs and plays the grow reveal.
    if (status === "loading") {
      return;
    }
    const staggerMs = data.length > 1 ? animationDuration * 0.4 : 0;
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, animationDuration + staggerMs);
    return () => clearTimeout(timer);
  }, [animationDuration, holdReveal, revealSignature, status]);

  useEffect(() => {
    onPhaseChange?.(isLoaded ? "ready" : "revealing");
  }, [isLoaded, onPhaseChange]);

  const hasProjection = projectionMode !== undefined;

  useEffect(() => {
    if (!hasProjection) {
      return;
    }
    if (isLoaded) {
      reveal.jump(1);
      return;
    }
    reveal.jump(0);
    if (holdReveal || status === "loading") {
      return;
    }
    const staggerMs = data.length > 1 ? animationDuration * 0.4 : 0;
    const controls = animateValue(reveal, 1, {
      duration: (animationDuration + staggerMs) / 1000,
      ease: "linear",
    });
    return () => controls.stop();
  }, [
    hasProjection,
    isLoaded,
    revealEpoch,
    holdReveal,
    status,
    animationDuration,
    data.length,
    reveal,
  ]);

  const resolveTooltipFromPos = useCallback(
    (pos: number): TooltipData | null => {
      // Find which band the pointer is over
      const bandIndex = Math.floor(pos / columnWidth);
      const clampedIndex = Math.max(0, Math.min(data.length - 1, bandIndex));
      const d = data[clampedIndex];

      if (!d) {
        return null;
      }

      // Calculate positions for each bar
      const yPositions: Record<string, number> = {};
      const xPositions: Record<string, number> = {};
      const barPos = categoryScale(categoryAccessor(d)) ?? 0;

      if (isHorizontal) {
        // Horizontal bars: dots at end of bar (x = value), centered vertically in band
        const seriesCount = lines.length;
        const groupGap = seriesCount > 1 ? 4 : 0;
        const individualBarHeight =
          seriesCount > 0
            ? (bandWidth - groupGap * (seriesCount - 1)) / seriesCount
            : bandWidth;

        if (stacked) {
          // Stacked horizontal: all bars same y, x at cumulative end
          let cumulative = 0;
          for (const line of lines) {
            const value = d[line.dataKey];
            if (typeof value === "number") {
              cumulative += value;
              const axisScale =
                yScales[normalizeYAxisId(line.yAxisId)] ?? valueScale;
              xPositions[line.dataKey] = axisScale(cumulative) ?? 0;
              yPositions[line.dataKey] = barPos + bandWidth / 2;
            }
          }
        } else {
          // Grouped horizontal: each bar at its own y position
          lines.forEach((line, idx) => {
            const value = d[line.dataKey];
            if (typeof value === "number") {
              const axisScale =
                yScales[normalizeYAxisId(line.yAxisId)] ?? valueScale;
              xPositions[line.dataKey] = axisScale(value) ?? 0;
              yPositions[line.dataKey] =
                barPos +
                idx * (individualBarHeight + groupGap) +
                individualBarHeight / 2;
            }
          });
        }
      } else if (stacked) {
        // Vertical stacked bars
        let cumulative = 0;
        let seriesIdx = 0;
        for (const line of lines) {
          const value = d[line.dataKey];
          if (typeof value === "number") {
            cumulative += value;
            const axisScale =
              yScales[normalizeYAxisId(line.yAxisId)] ?? primaryYScale;
            const gapOffset = seriesIdx * stackGap;
            yPositions[line.dataKey] = (axisScale(cumulative) ?? 0) - gapOffset;
            seriesIdx++;
          }
        }
      } else {
        // Vertical grouped bars
        const seriesCount = lines.length;
        const groupGap = seriesCount > 1 ? 4 : 0;
        const individualBarWidth =
          seriesCount > 0
            ? (bandWidth - groupGap * (seriesCount - 1)) / seriesCount
            : bandWidth;

        lines.forEach((line, idx) => {
          const value = d[line.dataKey];
          if (typeof value === "number") {
            const axisScale =
              yScales[normalizeYAxisId(line.yAxisId)] ?? primaryYScale;
            const baselineY = axisScale(0) ?? innerHeight;
            const valueY = axisScale(value) ?? 0;
            const barLengthPx = baselineY - valueY;

            if (squareSnap && !isHorizontal && value > 0) {
              yPositions[line.dataKey] = topSquareCenterY({
                baselineY,
                barLengthPx,
                squareSize: individualBarWidth,
                gap: squareSnap.squareGap,
                fit: squareSnap.fit,
              });
            } else {
              yPositions[line.dataKey] = valueY;
            }

            xPositions[line.dataKey] =
              barPos +
              idx * (individualBarWidth + groupGap) +
              individualBarWidth / 2;
          }
        });
      }

      // Tooltip position: for horizontal, position at max bar end; for vertical, center of band
      let tooltipX: number;
      if (isHorizontal) {
        // Position tooltip at the end of the longest bar
        const maxX = Math.max(...Object.values(xPositions), 0);
        tooltipX = maxX;
      } else {
        tooltipX = barPos + bandWidth / 2;
      }

      return {
        point: d,
        index: clampedIndex,
        x: tooltipX,
        yPositions,
        xPositions: Object.keys(xPositions).length > 0 ? xPositions : undefined,
      };
    },
    [
      categoryScale,
      valueScale,
      data,
      lines,
      categoryAccessor,
      columnWidth,
      bandWidth,
      isHorizontal,
      stacked,
      stackGap,
      yScales,
      primaryYScale,
      squareSnap,
      innerHeight,
    ]
  );

  const resolvePointerPos = useCallback(
    (
      event: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>
    ): number | null => {
      let point: { x: number; y: number } | null = null;

      if ("touches" in event) {
        const touch = event.touches[0];
        if (!touch) {
          return null;
        }
        const svg = event.currentTarget.ownerSVGElement;
        if (!svg) {
          return null;
        }
        point = localPointFromSvg(svg, touch.clientX, touch.clientY);
      } else {
        point = localPointFromSvg(
          event.currentTarget.ownerSVGElement,
          event.clientX,
          event.clientY
        );
      }

      if (!point) {
        return null;
      }
      return isHorizontal ? point.y - margin.top : point.x - margin.left;
    },
    [isHorizontal, margin.left, margin.top]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGGElement>) => {
      const pos = resolvePointerPos(event);
      if (pos === null) {
        return;
      }
      const tooltip = resolveTooltipFromPos(pos);
      if (tooltip) {
        scheduleTooltip(tooltip);
      }
    },
    [resolvePointerPos, resolveTooltipFromPos, scheduleTooltip]
  );

  const handleMouseLeave = useCallback(() => {
    clearTooltip();
  }, [clearTooltip]);

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<SVGGElement>) => {
      interactionElRef.current = event.currentTarget;
      if (event.touches.length !== 1) {
        return;
      }
      const pos = resolvePointerPos(event);
      if (pos === null) {
        return;
      }
      const tooltip = resolveTooltipFromPos(pos);
      if (!tooltip) {
        return;
      }
      if (
        touchTooltipVisibleRef.current &&
        lastTappedIndexRef.current === tooltip.index
      ) {
        clearTooltip();
        touchTooltipVisibleRef.current = false;
        lastTappedIndexRef.current = null;
        return;
      }
      scheduleTooltip(tooltip);
      touchTooltipVisibleRef.current = true;
      lastTappedIndexRef.current = tooltip.index;
    },
    [resolvePointerPos, resolveTooltipFromPos, scheduleTooltip, clearTooltip]
  );

  const canInteract = isLoaded && (!hasProjection || projectionSettled);
  const bandInteraction = canInteract && !isIsometric;

  const cancelPendingLeave = useCallback(() => {
    if (pendingLeaveRef.current !== null) {
      cancelAnimationFrame(pendingLeaveRef.current);
      pendingLeaveRef.current = null;
    }
  }, []);

  useEffect(() => cancelPendingLeave, [cancelPendingLeave]);

  useEffect(() => {
    if (!hasProjection) {
      return;
    }
    cancelPendingLeave();
    clearTooltip();
    touchTooltipVisibleRef.current = false;
    lastTappedIndexRef.current = null;
    lastTappedElementRef.current = null;
  }, [hasProjection, isIsometric, cancelPendingLeave, clearTooltip]);

  const handleElementEnter = useCallback(
    (tooltip: TooltipData) => {
      cancelPendingLeave();
      scheduleTooltip(tooltip);
    },
    [cancelPendingLeave, scheduleTooltip]
  );

  const handleElementLeave = useCallback(() => {
    cancelPendingLeave();
    pendingLeaveRef.current = requestAnimationFrame(() => {
      pendingLeaveRef.current = null;
      clearTooltip();
    });
  }, [cancelPendingLeave, clearTooltip]);

  const handleElementTap = useCallback(
    (tooltip: TooltipData) => {
      interactionElRef.current = slotRootRef.current;
      cancelPendingLeave();
      const key = `${tooltip.index}:${Math.round(tooltip.x)}`;
      if (
        touchTooltipVisibleRef.current &&
        lastTappedElementRef.current === key
      ) {
        clearTooltip();
        touchTooltipVisibleRef.current = false;
        lastTappedElementRef.current = null;
        return;
      }
      scheduleTooltip(tooltip);
      touchTooltipVisibleRef.current = true;
      lastTappedElementRef.current = key;
    },
    [cancelPendingLeave, clearTooltip, scheduleTooltip]
  );

  useEffect(() => {
    if (!canInteract) {
      return;
    }
    const onDocPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "touch") {
        return;
      }
      if (!touchTooltipVisibleRef.current) {
        return;
      }
      const container = interactionElRef.current;
      if (!container) {
        return;
      }
      const target = event.target as Node | null;
      if (target && container.contains(target)) {
        return;
      }
      clearTooltip();
      touchTooltipVisibleRef.current = false;
      lastTappedIndexRef.current = null;
      lastTappedElementRef.current = null;
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown, true);
    };
  }, [canInteract, clearTooltip]);

  // Separate children into defs, pre-overlay, and post-overlay
  const defsChildren: ReactElement[] = [];
  const clipExcludedChildren: ReactElement[] = [];
  const underlayChildren: ReactElement[] = [];
  const preOverlayChildren: ReactElement[] = [];
  const postOverlayChildren: ReactElement[] = [];

  forEachChartChild(children, (child) => {
    const resolvedChild = resolveChartChildElement(child);

    if (isGradientDefComponent(child)) {
      defsChildren.push(child);
    } else if (isPatternDefComponent(child)) {
      preOverlayChildren.push(child);
    } else if (isPostOverlayComponent(resolvedChild)) {
      postOverlayChildren.push(resolvedChild);
    } else if (isClipExcludedComponent(resolvedChild)) {
      clipExcludedChildren.push(
        isChartClipPassthrough(child.type) ? resolvedChild : child
      );
    } else if (isUnderlayComponent(resolvedChild)) {
      underlayChildren.push(resolvedChild);
    } else {
      preOverlayChildren.push(child);
    }
  });

  const referenceAreas = useMemo(
    () => extractReferenceAreaConfigs(children),
    [children]
  );

  /**
   * While the gate holds, the series layer stays unmounted, so its enter
   * animation starts on the commit that releases it. Grid, axes and reference
   * areas keep rendering — they sit outside the series clip in the
   * time-series shell for the same reason.
   */
  const seriesLayer = holdReveal
    ? null
    : renderKeyedChartLayers(preOverlayChildren);

  const projectionLanes = stacked ? 1 : Math.max(1, lines.length);
  const categoryStep =
    (categoryScale as unknown as { step?: () => number }).step?.() ??
    bandWidth;
  const laneDepth =
    projectionDepth ??
    Math.max(
      1,
      Math.min(categoryStep, (innerWidth * MAX_DEPTH_SHARE) / projectionLanes)
    );

  const slotOrder = useMemo(() => {
    if (!hasProjection) {
      return [];
    }
    const keys: string[] = [];
    const ranks: number[] = [];
    for (let category = 0; category < data.length; category++) {
      for (let lane = 0; lane < projectionLanes; lane++) {
        keys.push(barSlotKey(category, lane));
        ranks.push(painterKey(category, lane));
      }
    }
    return painterOrder(ranks).map((index) => keys[index] as string);
  }, [hasProjection, data.length, projectionLanes]);

  useLayoutEffect(() => {
    if (!hasProjection) {
      return;
    }
    const root = slotRootRef.current;
    const next = new Map<string, SVGGElement>();
    if (root) {
      for (const child of Array.from(root.children)) {
        const key = child.getAttribute("data-slot");
        if (key) {
          next.set(key, child as SVGGElement);
        }
      }
    }
    setSlots(next);
  }, [hasProjection, slotOrder]);

  const barProjection = useMemo<BarProjectionValue | null>(
    () =>
      hasProjection
        ? {
            slots,
            lanes: projectionLanes,
            laneDepth,
            interactive: canInteract && isIsometric,
            reveal,
            enter: handleElementEnter,
            leave: handleElementLeave,
            tap: handleElementTap,
          }
        : null,
    [
      hasProjection,
      slots,
      projectionLanes,
      laneDepth,
      canInteract,
      isIsometric,
      reveal,
      handleElementEnter,
      handleElementLeave,
      handleElementTap,
    ]
  );

  const contextValue = {
    ...DEFAULT_CHART_LIFECYCLE,
    chartPhase: resolveRestingChartPhase(status),
    chartStatus: status,
    data,
    renderData: data,
    xScale: fakeTimeScale as unknown as ReturnType<
      typeof import("@visx/scale").scaleTime<number>
    >,
    xScaleKind: "time" as const,
    yScale: isHorizontal ? valueScale : primaryYScale,
    yScales,
    width,
    height,
    innerWidth,
    innerHeight,
    margin,
    columnWidth,
    tooltipData,
    setTooltipData,
    containerRef,
    lines,
    referenceAreas,
    isLoaded,
    animationDuration,
    animationEasing,
    enterTransition,
    revealEpoch,
    xAccessor: xAccessorDate,
    dateLabels,
    // Bar-specific properties
    barScale: categoryScale,
    bandWidth,
    hoveredBarIndex,
    barXAccessor: categoryAccessor,
    orientation,
    stacked,
    stackOffsets,
    squareSnap,
  };

  const chartSvg = (
    <svg
      aria-hidden="true"
      className="overflow-visible"
      height={height}
      width={width}
    >
      {/* Gradient and pattern definitions */}
      {defsChildren.length > 0 && <defs>{defsChildren}</defs>}

      <rect fill="transparent" height={height} width={width} x={0} y={0} />

      {/* biome-ignore lint/a11y/noStaticElementInteractions: Chart interaction area */}
      <g
        onMouseLeave={
          bandInteraction && hoverCapable ? handleMouseLeave : undefined
        }
        onMouseMove={
          bandInteraction && hoverCapable ? handleMouseMove : undefined
        }
        onTouchStart={bandInteraction ? handleTouchStart : undefined}
        style={{
          cursor: bandInteraction ? "crosshair" : "default",
          touchAction: "manipulation",
        }}
        transform={`translate(${margin.left},${margin.top})`}
      >
        {/* Background rect for mouse event detection */}
        <rect
          fill="transparent"
          height={innerHeight}
          width={innerWidth}
          x={0}
          y={0}
        />

        {renderKeyedChartLayers(clipExcludedChildren)}
        {hasProjection ? (
          <ProjectionFadeOut>
            {renderKeyedChartLayers(underlayChildren)}
          </ProjectionFadeOut>
        ) : (
          renderKeyedChartLayers(underlayChildren)
        )}
        {status === "loading" ? (
          <BarLoadingSkeleton
            barCount={data.length || FALLBACK_LOADING_BARS}
            innerHeight={innerHeight}
            innerWidth={innerWidth}
          />
        ) : (
          seriesLayer
        )}
        {hasProjection && (
          <g ref={slotRootRef}>
            {slotOrder.map((key) => (
              <g data-slot={key} key={key} />
            ))}
          </g>
        )}

        {/* Markers rendered last so they're on top for interaction */}
        {renderKeyedChartLayers(postOverlayChildren)}
      </g>
    </svg>
  );

  return (
    <ChartProvider value={contextValue}>
      {projectionMode !== undefined ? (
        <ChartProjectionProvider
          depth={projectionLanes * laneDepth}
          height={innerHeight}
          kind="bar"
          mode={projectionMode}
          onSettledChange={setProjectionSettled}
          pad={PROJECTION_PAD}
          width={innerWidth}
          zExtent={innerHeight}
        >
          <BarProjectionProvider value={barProjection}>
            {chartSvg}
          </BarProjectionProvider>
        </ChartProjectionProvider>
      ) : (
        chartSvg
      )}
    </ChartProvider>
  );
});

export function BarChart({
  data,
  xDataKey = "name",
  margin: marginProp,
  animationDuration = 1100,
  animationEasing = DEFAULT_ANIMATION_EASING,
  enterTransition,
  revealSignature,
  aspectRatio = "2 / 1",
  className = "",
  barGap = 0.2,
  barWidth,
  orientation = "vertical",
  stacked = false,
  stackGap = 0,
  squareSnap,
  children,
  onPhaseChange,
  status = "ready",
  projection,
  projectionDepth,
}: BarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const margin = { ...DEFAULT_MARGIN, ...marginProp };

  return (
    <div
      className={cn("relative w-full overflow-visible", className)}
      ref={containerRef}
      style={{ aspectRatio, touchAction: "manipulation" }}
    >
      <ParentSize debounceTime={10}>
        {({ width, height }) => (
          <ChartInner
            animationDuration={animationDuration}
            animationEasing={animationEasing}
            barGap={barGap}
            barWidthProp={barWidth}
            containerRef={containerRef}
            data={data}
            enterTransition={enterTransition}
            height={height}
            margin={margin}
            onPhaseChange={onPhaseChange}
            orientation={orientation}
            projection={projection}
            projectionDepth={projectionDepth}
            revealSignature={revealSignature}
            squareSnap={squareSnap}
            stacked={stacked}
            stackGap={stackGap}
            status={status}
            width={width}
            xDataKey={xDataKey}
          >
            {children}
          </ChartInner>
        )}
      </ParentSize>
    </div>
  );
}

BarChart.displayName = "BarChart";

export default BarChart;
